import { useMemo, useRef, useState, useEffect, type ReactNode, type ComponentType } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

import { PageLayout } from '@components/layouts/PageLayout'
import { Button, Alert, Dialog } from '@atoms'
import { SelectField } from '@components/molecules/SelectField'
import ReactSelect, { components, type MultiValue, type SingleValue, type OptionProps, type CSSObjectWithLabel } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import Icon from '@components/utils/Icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms/Tooltip'
import { SimpleStatCard, SplitStatCard, BreakdownLine } from '@components/molecules/StatCard'
import { DropdownMenuItem, DropdownMenuSeparator } from '@components/atoms/DropdownMenu'
import { DotsMenu } from '@components/molecules/DotsMenu'

/**
 * Shifts manager (design-phase prototype)
 *
 * A redesign of the event roster page, explored inside the app shell. Everything
 * here is self-contained: sample data shaped like the getEventWithShifts query
 * (event > roster > shifts > shiftSlots > supporter), and small local components
 * standing in for the eventual real ones. The "Design tools" overlay at
 * bottom-left compares the remaining layout options and is dev-only.
 */

type Supporter = { id: string; firstName: string; fullName: string }
type Slot = { id: string; supporter: Supporter | null }
type Shift = {
  id: string
  roleName: string
  startTime: string
  endTime: string
  draft: boolean
  allowSelfSelect: boolean
  slots: Slot[]
  changed?: boolean // published, but edited since, so it has changes to re-publish
}
type EventData = {
  id: string
  name: string
  startTime: string
  endTime: string
  dates: string[]
  shifts: Shift[]
}

const SUPPORTERS: Supporter[] = [
  { id: 's1', firstName: 'Maria', fullName: 'Maria Rossi' },
  { id: 's2', firstName: 'Luca', fullName: 'Luca Bianchi' },
  { id: 's3', firstName: 'Giulia', fullName: 'Giulia Esposito' },
  { id: 's4', firstName: 'Alessandro', fullName: 'Alessandro Conti' },
  { id: 's5', firstName: 'Sofia', fullName: 'Sofia Ricci' },
  { id: 's6', firstName: 'Marco', fullName: 'Marco Ferrari' },
  { id: 's7', firstName: 'Elena', fullName: 'Elena Costa' },
  { id: 's8', firstName: 'Paolo', fullName: 'Paolo Gallo' },
  { id: 's9', firstName: 'Chiara', fullName: 'Chiara Greco' },
  { id: 's10', firstName: 'Francesca', fullName: 'Francesca Romano' },
  { id: 's11', firstName: 'Davide', fullName: 'Davide Marino' },
  { id: 's12', firstName: 'Valentina', fullName: 'Valentina Bruno' },
]

const byId = (id: string) => SUPPORTERS.find((s) => s.id === id) ?? null
const slot = (id: string, supporterId?: string): Slot => ({ id, supporter: supporterId ? byId(supporterId) : null })

// Sequence for ids created at runtime (adding / duplicating shifts in the preview).
let SEQ = 1
const nextId = (prefix: string) => `${prefix}-new-${SEQ++}`

// Filters are mirrored to the URL so a filtered view is shareable.
function readParam(key: string): string | null {
  try { return new URLSearchParams(window.location.search).get(key) } catch { return null }
}
function readListParam(key: string): string[] {
  const v = readParam(key)
  return v ? v.split(',').filter(Boolean) : []
}

// Two days, a mix of fully staffed / partial / empty and draft / published.
// Sofia (s5) sits on two overlapping Saturday shifts on purpose, to trip the
// double-booking check.
const D1 = '2026-06-27'
const D2 = '2026-06-28'
const iso = (date: string, time: string) => `${date}T${time}:00`

const EVENT: EventData = {
  id: 'e1',
  name: 'Reformist House Community Weekend',
  startTime: iso(D1, '08:00'),
  endTime: iso(D2, '17:00'),
  dates: [D1, D2],
  shifts: [
    {
      id: 'sh1', roleName: 'Setup crew', startTime: iso(D1, '08:00'), endTime: iso(D1, '10:00'),
      draft: false, allowSelfSelect: false,
      slots: [slot('sl1', 's6'), slot('sl2', 's11'), slot('sl3', 's5')],
    },
    {
      id: 'sh2', roleName: 'Registration desk', startTime: iso(D1, '09:30'), endTime: iso(D1, '13:00'),
      draft: false, allowSelfSelect: true,
      slots: [slot('sl4', 's1'), slot('sl5', 's9'), slot('sl6', 's5'), slot('sl7')],
    },
    {
      id: 'sh3', roleName: 'Door knocking', startTime: iso(D1, '10:00'), endTime: iso(D1, '13:00'),
      draft: false, allowSelfSelect: true,
      slots: [slot('sl8', 's2'), slot('sl9'), slot('sl10'), slot('sl11'), slot('sl12'), slot('sl13')],
    },
    {
      id: 'sh4', roleName: 'Marshals', startTime: iso(D1, '13:00'), endTime: iso(D1, '17:00'),
      draft: false, allowSelfSelect: false,
      slots: [slot('sl14', 's7'), slot('sl15', 's8')],
    },
    {
      id: 'sh6', roleName: 'Welcome desk', startTime: iso(D2, '09:00'), endTime: iso(D2, '12:00'),
      draft: true, allowSelfSelect: true,
      slots: [slot('sl20', 's10'), slot('sl21', 's12'), slot('sl22')],
    },
    {
      id: 'sh5', roleName: 'Pack down', startTime: iso(D2, '15:00'), endTime: iso(D2, '17:00'),
      draft: true, allowSelfSelect: false,
      slots: [slot('sl16'), slot('sl17'), slot('sl18'), slot('sl19')],
    },
  ],
}

const dt = (s: string) => new Date(s)
const fmtTime = (s: string) =>
  dt(s).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, '').toLowerCase()
const fmtRange = (a: string, b: string) => `${fmtTime(a)} to ${fmtTime(b)}`
const fmtDayLong = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })
const fmtDayShort = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
const dateOf = (s: string) => s.slice(0, 10)
const timeValue = (s: string) => s.slice(11, 16)
const overlaps = (a: Shift, b: Shift) => dt(a.startTime) < dt(b.endTime) && dt(b.startTime) < dt(a.endTime)

const filledOf = (sh: Shift) => sh.slots.filter((sl) => sl.supporter).length
const coveragePct = (sh: Shift) => (sh.slots.length === 0 ? 0 : filledOf(sh) / sh.slots.length)
type Cover = 'empty' | 'partial' | 'full'
const coverState = (sh: Shift): Cover => {
  const f = filledOf(sh)
  if (f === 0) return 'empty'
  if (f >= sh.slots.length) return 'full'
  return 'partial'
}
const COVER_BAR: Record<Cover, string> = {
  empty: 'tw:bg-error-500',
  partial: 'tw:bg-warning-500',
  full: 'tw:bg-success-500',
}
const COVER_TRACK: Record<Cover, string> = {
  empty: 'tw:bg-error-100',
  partial: 'tw:bg-warning-100',
  full: 'tw:bg-success-100',
}

// Issues carry the structured detail the fix-it walkthrough needs to act, plus a
// plain-language label for the alert and per-shift tooltip.
type OverlapIssue = {
  kind: 'overlap'
  id: string
  label: string
  shiftIds: string[]
  supporter: Supporter
  a: { shiftId: string; slotId: string; role: string }
  b: { shiftId: string; slotId: string; role: string }
}
type UnfilledIssue = {
  kind: 'unfilled-published'
  id: string
  label: string
  shiftIds: string[]
  shiftId: string
  role: string
  openCount: number
}
type Issue = OverlapIssue | UnfilledIssue

function computeIssues(shifts: Shift[]): Issue[] {
  const issues: Issue[] = []

  const seen = new Set<string>()
  for (let i = 0; i < shifts.length; i++) {
    for (let j = i + 1; j < shifts.length; j++) {
      const a = shifts[i]
      const b = shifts[j]
      if (!overlaps(a, b)) continue
      const aSlotByPerson = new Map<string, string>()
      a.slots.forEach((sl) => { if (sl.supporter) aSlotByPerson.set(sl.supporter.id, sl.id) })
      for (const sl of b.slots) {
        const person = sl.supporter
        if (person && aSlotByPerson.has(person.id)) {
          const id = `overlap:${[person.id, a.id, b.id].sort().join(':')}`
          if (seen.has(id)) continue
          seen.add(id)
          issues.push({
            kind: 'overlap',
            id,
            supporter: person,
            a: { shiftId: a.id, slotId: aSlotByPerson.get(person.id)!, role: a.roleName },
            b: { shiftId: b.id, slotId: sl.id, role: b.roleName },
            label: `${person.fullName} is on two shifts at once. ${a.roleName} and ${b.roleName} overlap.`,
            shiftIds: [a.id, b.id],
          })
        }
      }
    }
  }

  for (const sh of shifts) {
    if (!sh.draft && filledOf(sh) < sh.slots.length) {
      const gap = sh.slots.length - filledOf(sh)
      issues.push({
        kind: 'unfilled-published',
        id: `unfilled:${sh.id}`,
        shiftId: sh.id,
        role: sh.roleName,
        openCount: gap,
        label: `${sh.roleName} is published with ${gap} ${gap === 1 ? 'spot' : 'spots'} still open.`,
        shiftIds: [sh.id],
      })
    }
  }

  return issues
}

const shortIssueLabel = (iss: Issue) =>
  iss.kind === 'overlap' ? `${iss.supporter.firstName} is double-booked` : `${iss.role} needs people`

// --- Small shared pieces -------------------------------------------------

function Avatar({ name }: { name: string }) {
  const initials = name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('')
  return (
    <span aria-hidden className="tw:flex tw:h-6 tw:w-6 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-primary-100 tw:text-[0.625rem] tw:font-semibold tw:text-primary-700">
      {initials}
    </span>
  )
}

function StatusPill({ draft, changed }: { draft: boolean; changed?: boolean }) {
  if (draft) {
    return (
      <span className="tw:inline-flex tw:items-center tw:gap-1 tw:whitespace-nowrap tw:rounded-full tw:bg-gray-100 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:text-gray-600">
        <span aria-hidden className="tw:h-1.5 tw:w-1.5 tw:rounded-full tw:bg-gray-400" /> Draft
      </span>
    )
  }
  if (changed) {
    return (
      <span className="tw:inline-flex tw:items-center tw:gap-1 tw:whitespace-nowrap tw:rounded-full tw:bg-warning-50 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:text-warning-700">
        <Icon type="alert-circle" className="tw:h-3 tw:w-3" aria-hidden /> Changes to publish
      </span>
    )
  }
  return (
    <span className="tw:inline-flex tw:items-center tw:gap-1 tw:whitespace-nowrap tw:rounded-full tw:bg-success-50 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:text-success-700">
      <Icon type="check" className="tw:h-3 tw:w-3" aria-hidden /> Published
    </span>
  )
}

// Hover/focus hint for an icon-only control. The child must be a focusable
// element with its own accessible name (aria-label) so keyboard users get it too.
function IconHint({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

// The warning marker on a flagged shift. Hovering or focusing it shows the
// specific validation problems for that shift.
function ValidationFlag({ issues }: { issues: Issue[] }) {
  if (!issues.length) return null
  const label = `${issues.length} ${issues.length === 1 ? 'problem' : 'problems'} on this shift`
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="tw:flex tw:items-center tw:rounded tw:text-warning-500 tw:hover:text-warning-700 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-warning-400"
        >
          <Icon type="alert-triangle" className="tw:h-4 tw:w-4" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <ul className="tw:flex tw:flex-col tw:gap-1.5">
          {issues.map((iss) => (
            <li key={iss.id} className="tw:flex tw:items-start tw:gap-1.5">
              <span aria-hidden className="tw:mt-1 tw:h-1 tw:w-1 tw:shrink-0 tw:rounded-full tw:bg-warning-300" />
              {iss.label}
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  )
}

function CoverageMeter({ sh }: { sh: Shift }) {
  const state = coverState(sh)
  const f = filledOf(sh)
  const total = sh.slots.length
  const pct = Math.round(coveragePct(sh) * 100)
  return (
    <div role="img" aria-label={`${f} of ${total} spots filled`} className="tw:flex tw:w-28 tw:flex-col tw:gap-1">
      <div className="tw:flex tw:items-center tw:justify-between tw:text-xs tw:font-semibold tw:text-gray-600">
        <span aria-hidden>{f}/{total}</span>
      </div>
      <div aria-hidden className={clsx('tw:h-1.5 tw:w-full tw:overflow-hidden tw:rounded-full', COVER_TRACK[state])}>
        <div className={clsx('tw:h-full tw:rounded-full', COVER_BAR[state])} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// Compact searchable supporter list, reused by the inline assign popover and the
// fix-it walkthrough.
function SupporterPicker({ query, setQuery, onPick, autoFocus = true }: { query: string; setQuery: (q: string) => void; onPick: (s: Supporter) => void; autoFocus?: boolean }) {
  const matches = SUPPORTERS.filter((s) => s.fullName.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 6)
  return (
    <div>
      <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-gray-300 tw:px-2 tw:py-1.5 tw:focus-within:border-primary-400">
        <Icon type="search-lg" width={16} height={16} className="tw:text-gray-400" aria-hidden />
        <input
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search supporters"
          aria-label="Search supporters to assign"
          className="tw:w-full tw:bg-transparent tw:text-sm tw:text-gray-900 tw:placeholder-gray-400 tw:focus:outline-hidden"
        />
      </div>
      <div className="tw:max-h-48 tw:overflow-y-auto">
        {matches.length === 0 ? (
          <p className="tw:px-2 tw:py-3 tw:text-center tw:text-xs tw:text-gray-400">No matches</p>
        ) : (
          matches.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onPick(s)}
              className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-lg tw:px-2 tw:py-1.5 tw:text-left tw:text-sm tw:text-gray-700 tw:hover:bg-primary-50 tw:hover:text-primary-700"
            >
              <Avatar name={s.fullName} />
              {s.fullName}
            </button>
          ))
        )}
      </div>
    </div>
  )
}

// Portaled to the body with fixed positioning so the overflow-hidden roster
// wrapper can't clip it, and it stacks above the board.
function AssignPopover({ coords, onPick, onClose }: { coords: { top: number; left: number }; onPick: (s: Supporter) => void; onClose: () => void }) {
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onClose, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onClose, true)
    }
  }, [onClose])
  return createPortal(
    <div className="tailwind">
      <div
        ref={ref}
        role="dialog"
        aria-label="Assign a supporter"
        style={{ position: 'fixed', top: coords.top, left: coords.left }}
        className="tw:z-50 tw:w-64 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-2 tw:shadow-xl"
      >
        <SupporterPicker query={q} setQuery={setQ} onPick={onPick} />
      </div>
    </div>,
    document.body
  )
}

function SlotChip({ s, onAssign, onRemove }: { s: Slot; onAssign: (sup: Supporter) => void; onRemove: () => void }) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  if (s.supporter) {
    return (
      <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-full tw:border tw:border-success-200 tw:bg-success-50 tw:py-0.5 tw:pl-0.5 tw:pr-1.5 tw:text-xs tw:font-medium tw:text-success-800">
        <Avatar name={s.supporter.fullName} />
        {s.supporter.fullName}
        <IconHint label={`Remove ${s.supporter.fullName}`}>
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${s.supporter.fullName}`}
            className="tw:ml-0.5 tw:flex tw:items-center tw:rounded tw:text-success-500 tw:hover:text-error-600 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-error-400"
          >
            <Icon type="x-close" className="tw:h-3 tw:w-3" aria-hidden />
          </button>
        </IconHint>
      </span>
    )
  }
  const toggle = () => {
    if (open) { setOpen(false); return }
    const r = btnRef.current?.getBoundingClientRect()
    if (r) setCoords({ top: r.bottom + 4, left: r.left })
    setOpen(true)
  }
  return (
    <span className="tw:inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="tw:inline-flex tw:items-center tw:gap-1 tw:rounded-full tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium tw:text-gray-500 tw:hover:border-primary-400 tw:hover:text-primary-600 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-400"
      >
        <Icon type="plus" className="tw:h-3 tw:w-3" aria-hidden /> Assign
      </button>
      {open && coords && (
        <AssignPopover coords={coords} onPick={(sup) => { onAssign(sup); setOpen(false) }} onClose={() => setOpen(false)} />
      )}
    </span>
  )
}

function PublishLink({ draft, changed, onPublish }: { draft: boolean; changed?: boolean; onPublish: () => void }) {
  if (!draft && !changed) return null
  return <Button rank="link" size="sm" leadingIcon="check" onClick={onPublish}>{draft ? 'Publish' : 'Publish changes'}</Button>
}

function ShiftMenu({ roleName, onEdit, onDuplicate, onMove, onDelete }: { roleName: string; onEdit: () => void; onDuplicate: () => void; onMove: () => void; onDelete: () => void }) {
  return (
    <DotsMenu ariaLabel={`More actions for ${roleName}`}>
      <DropdownMenuItem onSelect={onEdit}><Icon type="pencil-02" /> Edit shift</DropdownMenuItem>
      <DropdownMenuItem onSelect={onDuplicate}><Icon type="copy-06" /> Duplicate</DropdownMenuItem>
      <DropdownMenuItem onSelect={onMove}><Icon type="calendar" /> Move to day</DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem destructive onSelect={onDelete}><Icon type="trash-01" /> Delete shift</DropdownMenuItem>
    </DotsMenu>
  )
}

function ShiftActions({ draft, changed, roleName, onPublish, onEdit, onDuplicate, onMove, onDelete }: { draft: boolean; changed?: boolean; roleName: string; onPublish: () => void; onEdit: () => void; onDuplicate: () => void; onMove: () => void; onDelete: () => void }) {
  return (
    <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
      <PublishLink draft={draft} changed={changed} onPublish={onPublish} />
      <ShiftMenu roleName={roleName} onEdit={onEdit} onDuplicate={onDuplicate} onMove={onMove} onDelete={onDelete} />
    </div>
  )
}

function SlotChips({ shift, onAssign, onRemove }: { shift: Shift; onAssign: (slotId: string, sup: Supporter) => void; onRemove: (slotId: string) => void }) {
  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
      {shift.slots.map((sl) => (
        <SlotChip key={sl.id} s={sl} onAssign={(sup) => onAssign(sl.id, sup)} onRemove={() => onRemove(sl.id)} />
      ))}
    </div>
  )
}

// Self-select is the default once published, so we only flag the exception:
// shifts a coordinator must assign (no public self-select).
function AssignOnlyBadge() {
  return (
    <IconHint label="Volunteers can't self-select this shift. A coordinator assigns it.">
      <span tabIndex={0} role="note" aria-label="Staff assign only" className="tw:inline-flex tw:shrink-0 tw:items-center tw:gap-1 tw:whitespace-nowrap tw:rounded-full tw:bg-gray-100 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:text-gray-600 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-400">
        <Icon type="user-square" className="tw:h-3 tw:w-3" aria-hidden /> Staff only
      </span>
    </IconHint>
  )
}

// --- Shift presentations: list row and card ------------------------------

type ShiftViewProps = {
  sh: Shift
  issues: Issue[]
  selected: boolean
  onToggleSelect: () => void
  onAssign: (slotId: string, sup: Supporter) => void
  onRemove: (slotId: string) => void
  onPublish: () => void
  onEdit: () => void
  onDuplicate: () => void
  onMove: () => void
  onDelete: () => void
}

function ShiftListRow({ sh, issues, selected, onToggleSelect, onAssign, onRemove, onPublish, onEdit, onDuplicate, onMove, onDelete, compact }: ShiftViewProps & { compact: boolean }) {
  return (
    <div
      className={clsx(
        'tw:flex tw:items-start tw:gap-3 tw:px-4 tw:hover:bg-gray-50',
        compact ? 'tw:py-2' : 'tw:py-3',
        selected && 'tw:bg-primary-50/40'
      )}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggleSelect}
        aria-label={`Select ${sh.roleName}`}
        className="tw:mt-1 tw:h-4 tw:w-4 tw:shrink-0 tw:rounded tw:border-gray-300 tw:text-primary-600"
      />
      <div className="tw:w-48 tw:shrink-0">
        <div className="tw:flex tw:items-center tw:gap-1.5">
          <span className="tw:font-semibold tw:text-gray-900">{sh.roleName}</span>
          <ValidationFlag issues={issues} />
        </div>
        <div className="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-gray-500">
          <Icon type="clock" className="tw:h-3 tw:w-3" aria-hidden />
          {fmtRange(sh.startTime, sh.endTime)}
        </div>
        <div className="tw:mt-1.5 tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
          <StatusPill draft={sh.draft} changed={sh.changed} />
          {!sh.allowSelfSelect && <AssignOnlyBadge />}
        </div>
      </div>
      <div className="tw:shrink-0 tw:pt-0.5">
        <CoverageMeter sh={sh} />
      </div>
      <div className="tw:flex-1 tw:pt-0.5">
        <SlotChips shift={sh} onAssign={onAssign} onRemove={onRemove} />
      </div>
      <ShiftActions draft={sh.draft} changed={sh.changed} roleName={sh.roleName} onPublish={onPublish} onEdit={onEdit} onDuplicate={onDuplicate} onMove={onMove} onDelete={onDelete} />
    </div>
  )
}

function ShiftCard({ sh, issues, selected, onToggleSelect, onAssign, onRemove, onPublish, onEdit, onDuplicate, onMove, onDelete }: ShiftViewProps) {
  return (
    <div
      className={clsx(
        'tw:flex tw:flex-col tw:gap-3 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-4 tw:shadow-xs',
        selected && 'tw:ring-2 tw:ring-primary-300'
      )}
    >
      {/* Identity + menu */}
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-2">
        <div className="tw:flex tw:min-w-0 tw:items-start tw:gap-2">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            aria-label={`Select ${sh.roleName}`}
            className="tw:mt-1 tw:h-4 tw:w-4 tw:shrink-0 tw:rounded tw:border-gray-300 tw:text-primary-600"
          />
          <div className="tw:min-w-0">
            <div className="tw:flex tw:items-center tw:gap-1.5">
              <span className="tw:truncate tw:font-semibold tw:text-gray-900">{sh.roleName}</span>
              <ValidationFlag issues={issues} />
            </div>
            <div className="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-gray-500">
              <Icon type="clock" className="tw:h-3 tw:w-3" aria-hidden />
              {fmtRange(sh.startTime, sh.endTime)}
            </div>
          </div>
        </div>
        <ShiftMenu roleName={sh.roleName} onEdit={onEdit} onDuplicate={onDuplicate} onMove={onMove} onDelete={onDelete} />
      </div>

      {/* Status (left) + coverage (right), consistent positions for scanning */}
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
          <StatusPill draft={sh.draft} changed={sh.changed} />
          {!sh.allowSelfSelect && <AssignOnlyBadge />}
        </div>
        <CoverageMeter sh={sh} />
      </div>

      <SlotChips shift={sh} onAssign={onAssign} onRemove={onRemove} />

      {/* Publish action sits in a consistent footer when relevant */}
      {(sh.draft || sh.changed) && (
        <div className="tw:flex tw:justify-end tw:border-t tw:border-gray-100 tw:pt-2">
          <PublishLink draft={sh.draft} changed={sh.changed} onPublish={onPublish} />
        </div>
      )}
    </div>
  )
}

// --- Add / edit shift dialogs (workshopped, self-contained) --------------

type DraftRow = { startTime: string; endTime: string; roleName: string; volunteers: number }
const blankRow = (): DraftRow => ({ startTime: '09:00', endTime: '12:00', roleName: '', volunteers: 1 })

function FieldLabel({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return <label htmlFor={htmlFor} className="tw:text-xs tw:font-medium tw:text-gray-600">{children}</label>
}
const inputCls = 'tw:rounded-lg tw:border tw:border-gray-400 tw:px-2 tw:py-1.5 tw:text-sm tw:focus:border-primary-400 tw:focus:outline-hidden'

type SelectOption = { value: string; label: string }

// react-select styling matched to the v2 Select atom (components/atoms/form/Select)
// so these dropdowns read identically to the ones used across v2. We can't use the
// atom directly: it's single-select only, and these need multi-select (filters) and
// creatable (role). Everything not overridden falls back to react-select's defaults,
// which is what the atom relies on too — so the menus match without restating them.
const selectClassNames = {
  control: () =>
    'tw:border tw:rounded tw:border-gray-400 tw:focus-within:ring-4 tw:focus-within:border-primary-400 tw:ring-primary-100',
  input: () => 'tw:m-0 tw:p-0',
  placeholder: () => 'tw:whitespace-nowrap',
  singleValue: () => 'tw:m-0',
  valueContainer: () => 'tw:px-3 tw:py-2',
}

// react-select annotates its built-in components as @emotion JSX, which isn't
// assignable to React 19's stricter element type — re-type as a plain component
// (same workaround the v2 Select atom uses for MenuList).
const RsOption = components.Option as unknown as ComponentType<OptionProps<SelectOption, true>>

// Filter menu option with a checkbox, so a count-trigger multi-select still shows
// what's ticked when reopened (hideSelectedOptions is off for the same reason).
const CheckboxOption = (props: OptionProps<SelectOption, true>) => (
  <RsOption {...props}>
    <span className="tw:flex tw:items-center tw:gap-2">
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        className="tw:pointer-events-none tw:h-4 tw:w-4 tw:rounded tw:border-gray-300 tw:text-primary-600"
      />
      {props.label}
    </span>
  </RsOption>
)

// Creatable role combobox: suggests existing roles so people reuse names instead of
// introducing typos, but still lets you add a new one. The menu portals into the
// dialog so the dialog's scroll container can't clip it and Radix keeps it
// interactive (a document.body portal sits outside the modal's pointer-events tree).
function RoleSelect({ id, value, onChange, roles, invalid }: { id?: string; value: string; onChange: (v: string) => void; roles: string[]; invalid?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [menuPortal, setMenuPortal] = useState<HTMLElement | null>(null)
  const menuOpenRef = useRef(false)
  useEffect(() => {
    setMenuPortal(wrapRef.current?.closest<HTMLElement>('[role="dialog"]') ?? document.body)
  }, [])
  const options = roles.map((r) => ({ value: r, label: r }))
  const selected = value ? { value, label: value } : null
  return (
    <div ref={wrapRef}>
      <CreatableSelect
        inputId={id}
        options={options}
        value={selected}
        onChange={(opt: SingleValue<SelectOption>) => onChange(opt?.value ?? '')}
        onCreateOption={(input) => onChange(input)}
        placeholder="e.g. Registration desk"
        isClearable
        formatCreateLabel={(input) => `Use "${input}"`}
        aria-invalid={invalid}
        // react-select inside a Radix dialog: when the menu is already closed, let
        // Escape bubble so the dialog closes; and keep focus from leaving the
        // trapped layer on blur. Mirrors the v2 Select atom's workarounds.
        onMenuOpen={() => { menuOpenRef.current = true }}
        onMenuClose={() => { menuOpenRef.current = false }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !menuOpenRef.current) {
            e.preventDefault()
            requestAnimationFrame(() => {
              document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true }))
            })
          }
        }}
        onBlur={(e) => {
          const el = e.relatedTarget as HTMLElement | null
          if (el && (el.tagName === 'A' || el.tagName === 'BUTTON' || el.tagName === 'INPUT')) el.focus()
        }}
        classNames={{
          ...selectClassNames,
          control: () =>
            clsx(
              'tw:border tw:rounded tw:focus-within:ring-4',
              invalid
                ? 'tw:border-error-400 tw:focus-within:border-error-500 tw:ring-error-100'
                : 'tw:border-gray-400 tw:focus-within:border-primary-400 tw:ring-primary-100'
            ),
        }}
        menuPortalTarget={menuPortal ?? undefined}
        menuPosition="fixed"
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) as CSSObjectWithLabel }}
      />
    </div>
  )
}

// Multi-select filter styled like the v2 Select. Selections live in the chip bar
// below the toolbar, so the control shows only a count (controlShouldRenderValue
// off) instead of rendering chips inline.
function MultiFilter({ label, options, selected, onChange }: { label: string; options: SelectOption[]; selected: string[]; onChange: (next: string[]) => void }) {
  const value = options.filter((o) => selected.includes(o.value))
  return (
    <ReactSelect
      isMulti
      options={options}
      value={value}
      onChange={(vals: MultiValue<SelectOption>) => onChange(vals.map((v) => v.value))}
      placeholder={selected.length ? `${label} (${selected.length})` : label}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      controlShouldRenderValue={false}
      backspaceRemovesValue={false}
      isClearable={false}
      components={{ Option: CheckboxOption }}
      classNames={selectClassNames}
      aria-label={label}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) as CSSObjectWithLabel }}
    />
  )
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <div className="tw:inline-flex tw:items-center tw:gap-2 tw:rounded-lg tw:bg-primary-50 tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:text-primary-700">
      {label}
      <button type="button" onClick={onRemove} aria-label={`Remove filter ${label}`} className="tw:flex tw:items-center tw:rounded tw:text-primary-500 tw:hover:text-primary-800 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-400">
        <Icon type="x-close" width={12} height={12} aria-hidden />
      </button>
    </div>
  )
}

// Generic confirm dialog for destructive actions.
type ConfirmState = { title: string; body: string; confirmLabel: string; onConfirm: () => void }
function ConfirmDialog({ state, onOpenChange }: { state: ConfirmState | null; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog.Root open={state !== null} onOpenChange={onOpenChange}>
      <Dialog.Content size="lg">
        <Dialog.Header>
          <Dialog.Title>{state?.title}</Dialog.Title>
          <Dialog.Description className="tw:text-gray-600">{state?.body}</Dialog.Description>
        </Dialog.Header>
        <Dialog.Footer className="tw:mt-2 tw:gap-2">
          <Dialog.Close asChild><Button rank="secondary">Cancel</Button></Dialog.Close>
          <Button rank="primary" color="danger" leadingIcon="trash-01" onClick={() => { state?.onConfirm(); onOpenChange(false) }}>
            {state?.confirmLabel}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

// Publishing makes shifts visible on the embedded RSVP forms so volunteers can
// self-select open spots. The dialog confirms that, and optionally notifies the
// people already assigned about their shifts.
function PublishDialog({ ids, shifts, onOpenChange, onConfirm }: { ids: string[] | null; shifts: Shift[]; onOpenChange: (open: boolean) => void; onConfirm: (ids: string[], notify: boolean) => void }) {
  const [notify, setNotify] = useState(true)
  useEffect(() => { if (ids) setNotify(true) }, [ids])
  const targetShifts = ids ? shifts.filter((s) => ids.includes(s.id) && (s.draft || s.changed)) : []
  const count = targetShifts.length
  const selfSelect = targetShifts.filter((s) => s.allowSelfSelect).length
  const staffOnly = count - selfSelect
  const assigned = targetShifts.reduce((n, sh) => n + filledOf(sh), 0)
  return (
    <Dialog.Root open={ids !== null} onOpenChange={onOpenChange}>
      <Dialog.Content size="lg">
        <Dialog.Header>
          <Dialog.Title>{count === 1 ? 'Publish this shift?' : `Publish ${count} shifts?`}</Dialog.Title>
          <Dialog.Description className="tw:text-gray-600">Publishing makes these shifts live.</Dialog.Description>
        </Dialog.Header>
        <ul className="tw:flex tw:flex-col tw:gap-2 tw:text-sm tw:text-gray-700">
          {selfSelect > 0 && (
            <li className="tw:flex tw:items-start tw:gap-2">
              <Icon type="check-circle" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-success-600" aria-hidden />
              {selfSelect} {selfSelect === 1 ? 'shift' : 'shifts'} will open for volunteers to self-select on your RSVP forms.
            </li>
          )}
          {staffOnly > 0 && (
            <li className="tw:flex tw:items-start tw:gap-2">
              <Icon type="user-square" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-400" aria-hidden />
              {staffOnly} staff-assign-only {staffOnly === 1 ? 'shift stays' : 'shifts stay'} coordinator-assigned.
            </li>
          )}
        </ul>
        {assigned > 0 && (
          <label className="tw:flex tw:items-start tw:gap-2 tw:rounded-lg tw:border tw:border-gray-200 tw:p-3 tw:text-sm tw:text-gray-700">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="tw:mt-0.5 tw:h-4 tw:w-4 tw:rounded tw:border-gray-300 tw:text-primary-600" />
            Email the {assigned} assigned {assigned === 1 ? 'volunteer' : 'volunteers'} about their {assigned === 1 ? 'shift' : 'shifts'} and any changes.
          </label>
        )}
        <Dialog.Footer className="tw:mt-2 tw:gap-2">
          <Dialog.Close asChild><Button rank="secondary">Cancel</Button></Dialog.Close>
          <Button rank="primary" leadingIcon="check" onClick={() => { if (ids) onConfirm(ids, notify); onOpenChange(false) }}>
            {count === 1 ? 'Publish shift' : `Publish ${count} shifts`}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

function MoveToDayDialog({ shift, dates, onOpenChange, onMove }: { shift: Shift | null; dates: string[]; onOpenChange: (open: boolean) => void; onMove: (id: string, date: string) => void }) {
  const [date, setDate] = useState(dates[0])
  useEffect(() => { if (shift) setDate(dateOf(shift.startTime)) }, [shift])
  return (
    <Dialog.Root open={shift !== null} onOpenChange={onOpenChange}>
      <Dialog.Content size="lg">
        <Dialog.Header>
          <Dialog.Title>Move shift to another day</Dialog.Title>
          {shift && <Dialog.Description className="tw:text-gray-600">{shift.roleName}, {fmtRange(shift.startTime, shift.endTime)}. The times stay the same.</Dialog.Description>}
        </Dialog.Header>
        <div className="tw:py-1"><DayToggle dates={dates} value={date} onChange={setDate} /></div>
        <Dialog.Footer className="tw:mt-2 tw:gap-2">
          <Dialog.Close asChild><Button rank="secondary">Cancel</Button></Dialog.Close>
          <Button rank="primary" onClick={() => { if (shift) onMove(shift.id, date); onOpenChange(false) }}>Move shift</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

function DayToggle({ dates, value, onChange }: { dates: string[]; value: string; onChange: (d: string) => void }) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1">
      <span className="tw:text-sm tw:font-medium tw:text-gray-700">Day</span>
      <div role="group" aria-label="Day" className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
        {dates.map((d, i) => (
          <button
            key={d}
            type="button"
            onClick={() => onChange(d)}
            aria-pressed={value === d}
            className={clsx('tw:px-3 tw:py-2 tw:text-sm tw:font-medium', i > 0 && 'tw:border-l tw:border-gray-300', value === d ? 'tw:bg-primary-50 tw:text-primary-700' : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50')}
          >
            {fmtDayShort(d)}
          </button>
        ))}
      </div>
    </div>
  )
}

function AddShiftsDialog({
  open, onOpenChange, dates, presetDate, roles, onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  dates: string[]
  presetDate: string | null
  roles: string[]
  onAdd: (shifts: Shift[]) => void
}) {
  const [date, setDate] = useState(presetDate ?? dates[0])
  const [rows, setRows] = useState<DraftRow[]>([blankRow()])
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (open) {
      setDate(presetDate ?? dates[0])
      setRows([blankRow()])
      setShowErrors(false)
    }
  }, [open, presetDate, dates])

  const updateRow = (i: number, patch: Partial<DraftRow>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  const addRow = () => setRows((prev) => [...prev, blankRow()])
  const removeRow = (i: number) => setRows((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev))

  const onSubmit = () => {
    const valid = rows.filter((r) => r.roleName.trim() && r.startTime && r.endTime && r.volunteers >= 1)
    if (valid.length === 0) {
      setShowErrors(true)
      return
    }
    onAdd(valid.map((r) => ({
      id: nextId('sh'),
      roleName: r.roleName.trim(),
      startTime: iso(date, r.startTime),
      endTime: iso(date, r.endTime),
      draft: true,
      allowSelfSelect: true,
      slots: Array.from({ length: Math.max(1, r.volunteers) }, () => ({ id: nextId('sl'), supporter: null })),
    })))
    onOpenChange(false)
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="2xl">
        <Dialog.Header>
          <Dialog.Title>Add shifts</Dialog.Title>
          <Dialog.Description className="tw:text-gray-500">
            New shifts are saved as drafts. Publish them when the roster is ready.
          </Dialog.Description>
        </Dialog.Header>

        <div className="tw:flex tw:flex-col tw:gap-4">
          {presetDate ? (
            <p className="tw:text-sm tw:text-gray-600">
              Adding to <span className="tw:font-semibold tw:text-gray-900">{fmtDayLong(presetDate)}</span>.
            </p>
          ) : (
            <DayToggle dates={dates} value={date} onChange={setDate} />
          )}

          <div className="tw:flex tw:max-h-[50vh] tw:flex-col tw:gap-3 tw:overflow-y-auto">
            {rows.map((r, i) => {
              const roleMissing = showErrors && !r.roleName.trim()
              return (
                <div key={i} className="tw:flex tw:flex-wrap tw:items-end tw:gap-3 tw:rounded-lg tw:border tw:border-gray-200 tw:p-3">
                  <div className="tw:flex tw:flex-col tw:gap-1">
                    <FieldLabel htmlFor={`start-${i}`}>Start</FieldLabel>
                    <input id={`start-${i}`} type="time" value={r.startTime} onChange={(e) => updateRow(i, { startTime: e.target.value })} className={inputCls} />
                  </div>
                  <div className="tw:flex tw:flex-col tw:gap-1">
                    <FieldLabel htmlFor={`end-${i}`}>End</FieldLabel>
                    <input id={`end-${i}`} type="time" value={r.endTime} onChange={(e) => updateRow(i, { endTime: e.target.value })} className={inputCls} />
                  </div>
                  <div className="tw:flex tw:min-w-40 tw:flex-1 tw:flex-col tw:gap-1">
                    <FieldLabel htmlFor={`role-${i}`}>Role</FieldLabel>
                    <RoleSelect id={`role-${i}`} value={r.roleName} onChange={(v) => updateRow(i, { roleName: v })} roles={roles} invalid={roleMissing} />
                  </div>
                  <div className="tw:flex tw:w-24 tw:flex-col tw:gap-1">
                    <FieldLabel htmlFor={`vol-${i}`}>Volunteers</FieldLabel>
                    <input id={`vol-${i}`} type="number" min={1} value={r.volunteers}
                      onChange={(e) => updateRow(i, { volunteers: Math.max(1, Number(e.target.value) || 1) })} className={inputCls} />
                  </div>
                  <IconHint label="Remove this row">
                    <button type="button" onClick={() => removeRow(i)} disabled={rows.length === 1} aria-label={`Remove shift ${i + 1}`}
                      className="tw:flex tw:h-9 tw:w-9 tw:items-center tw:justify-center tw:rounded-lg tw:text-gray-400 tw:hover:bg-gray-100 tw:hover:text-error-600 tw:disabled:opacity-40 tw:disabled:hover:bg-transparent tw:disabled:hover:text-gray-400">
                      <Icon type="trash-01" className="tw:h-4 tw:w-4" aria-hidden />
                    </button>
                  </IconHint>
                </div>
              )
            })}
          </div>

          {showErrors && <p className="tw:text-sm tw:text-error-600">Give each shift a role name before adding it.</p>}

          <div>
            <Button rank="link" leadingIcon="plus" onClick={addRow}>Add another shift</Button>
          </div>
        </div>

        <Dialog.Footer className="tw:mt-2 tw:gap-2">
          <Dialog.Close asChild><Button rank="secondary">Cancel</Button></Dialog.Close>
          <Button rank="primary" onClick={onSubmit}>Add shifts</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

type EditFields = { startTime: string; endTime: string; roleName: string; volunteers: number; allowSelfSelect: boolean }

function EditShiftDialog({
  shift, roles, onOpenChange, onSave,
}: {
  shift: Shift | null
  roles: string[]
  onOpenChange: (open: boolean) => void
  onSave: (id: string, fields: EditFields) => void
}) {
  const [fields, setFields] = useState<EditFields>({ startTime: '09:00', endTime: '12:00', roleName: '', volunteers: 1, allowSelfSelect: false })
  const [showErrors, setShowErrors] = useState(false)
  const assignedCount = shift ? filledOf(shift) : 0

  useEffect(() => {
    if (shift) {
      setFields({
        startTime: timeValue(shift.startTime),
        endTime: timeValue(shift.endTime),
        roleName: shift.roleName,
        volunteers: shift.slots.length,
        allowSelfSelect: shift.allowSelfSelect,
      })
      setShowErrors(false)
    }
  }, [shift])

  const onSubmit = () => {
    if (!shift) return
    if (!fields.roleName.trim()) { setShowErrors(true); return }
    onSave(shift.id, { ...fields, roleName: fields.roleName.trim() })
    onOpenChange(false)
  }

  return (
    <Dialog.Root open={shift !== null} onOpenChange={onOpenChange}>
      <Dialog.Content size="lg">
        <Dialog.Header>
          <Dialog.Title>Edit shift</Dialog.Title>
          {shift && (
            <Dialog.Description className="tw:text-gray-500">{fmtDayLong(dateOf(shift.startTime))}</Dialog.Description>
          )}
        </Dialog.Header>

        <div className="tw:flex tw:flex-col tw:gap-4">
          <div className="tw:flex tw:gap-3">
            <div className="tw:flex tw:flex-col tw:gap-1">
              <FieldLabel htmlFor="edit-start">Start</FieldLabel>
              <input id="edit-start" type="time" value={fields.startTime} onChange={(e) => setFields((f) => ({ ...f, startTime: e.target.value }))} className={inputCls} />
            </div>
            <div className="tw:flex tw:flex-col tw:gap-1">
              <FieldLabel htmlFor="edit-end">End</FieldLabel>
              <input id="edit-end" type="time" value={fields.endTime} onChange={(e) => setFields((f) => ({ ...f, endTime: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="tw:flex tw:flex-col tw:gap-1">
            <FieldLabel htmlFor="edit-role">Role</FieldLabel>
            <RoleSelect id="edit-role" value={fields.roleName} onChange={(v) => setFields((f) => ({ ...f, roleName: v }))} roles={roles} invalid={showErrors && !fields.roleName.trim()} />
          </div>
          <div className="tw:flex tw:w-32 tw:flex-col tw:gap-1">
            <FieldLabel htmlFor="edit-vol">Volunteers</FieldLabel>
            <input id="edit-vol" type="number" min={1} value={fields.volunteers}
              onChange={(e) => setFields((f) => ({ ...f, volunteers: Math.max(1, Number(e.target.value) || 1) }))} className={inputCls} />
          </div>
          {fields.volunteers < assignedCount && (
            <Alert type="warning" size="sm">
              {assignedCount} people are assigned. Lowering to {fields.volunteers} will unassign {assignedCount - fields.volunteers} of them.
            </Alert>
          )}
          <label className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-gray-700">
            <input type="checkbox" checked={fields.allowSelfSelect} onChange={(e) => setFields((f) => ({ ...f, allowSelfSelect: e.target.checked }))}
              className="tw:h-4 tw:w-4 tw:rounded tw:border-gray-300 tw:text-primary-600" />
            Let volunteers sign themselves up
          </label>
          {showErrors && !fields.roleName.trim() && <p className="tw:text-sm tw:text-error-600">A role name is required.</p>}
        </div>

        <Dialog.Footer className="tw:mt-2 tw:gap-2">
          <Dialog.Close asChild><Button rank="secondary">Cancel</Button></Dialog.Close>
          <Button rank="primary" onClick={onSubmit}>Save changes</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

// --- Fix-it stepped wizard (full page, accessible) -----------------------

type FixChoice = 'removeA' | 'removeB' | 'assign' | 'draft' | 'skip'

function WizardStepper({ steps, currentIndex, applied, onJump }: { steps: Issue[]; currentIndex: number; applied: Set<string>; onJump: (i: number) => void }) {
  return (
    <nav aria-label="Issues to fix" className="tw:rounded-xl tw:border tw:border-gray-200 tw:bg-gray-50 tw:p-4">
      <p className="tw:mb-3 tw:text-sm tw:font-semibold tw:text-gray-700">Issues to fix</p>
      <ol className="tw:flex tw:flex-col tw:gap-1">
        {steps.map((iss, i) => {
          const isCurrent = i === currentIndex
          const isDone = applied.has(iss.id)
          return (
            <li key={iss.id}>
              <button
                type="button"
                onClick={() => onJump(i)}
                aria-current={isCurrent ? 'step' : undefined}
                className={clsx(
                  'tw:flex tw:w-full tw:items-start tw:gap-2 tw:rounded-lg tw:px-2 tw:py-2 tw:text-left tw:text-sm tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-400',
                  isCurrent ? 'tw:bg-white tw:font-semibold tw:text-primary-800 tw:shadow-xs' : 'tw:text-gray-600 tw:hover:bg-white/70'
                )}
              >
                <Icon
                  type={isDone ? 'check-circle' : 'alert-circle'}
                  className={clsx('tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0', isDone ? 'tw:text-success-500' : isCurrent ? 'tw:text-primary-600' : 'tw:text-gray-300')}
                  aria-hidden
                />
                <span>{shortIssueLabel(iss)}</span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

function FixIssuesWizard({
  steps, remaining, onApply, onExit,
}: {
  steps: Issue[]
  remaining: number
  onApply: (issue: Issue, choice: FixChoice, supporter?: Supporter) => void
  onExit: () => void
}) {
  const [stepIndex, setStepIndex] = useState(0)
  const [choices, setChoices] = useState<Record<string, FixChoice>>({})
  const [supporters, setSupporters] = useState<Record<string, Supporter | undefined>>({})
  const [applied, setApplied] = useState<Set<string>>(new Set())
  const [query, setQuery] = useState('')
  const [needsSupporter, setNeedsSupporter] = useState(false)
  const [announce, setAnnounce] = useState('')
  const headingRef = useRef<HTMLHeadingElement>(null)

  const total = steps.length
  const onReview = stepIndex >= total
  const current = onReview ? null : steps[stepIndex]

  // Move focus to the step heading on each transition (screen-reader + keyboard).
  useEffect(() => {
    headingRef.current?.focus()
    setQuery('')
    setNeedsSupporter(false)
  }, [stepIndex])

  const choice = current ? choices[current.id] : undefined

  const optionsFor = (iss: Issue): Array<{ value: FixChoice; label: string }> =>
    iss.kind === 'overlap'
      ? [
          { value: 'removeA', label: `Take ${iss.supporter.firstName} off ${iss.a.role}` },
          { value: 'removeB', label: `Take ${iss.supporter.firstName} off ${iss.b.role}` },
          { value: 'skip', label: 'Leave it for now' },
        ]
      : [
          { value: 'assign', label: 'Assign a volunteer to fill a spot' },
          { value: 'draft', label: `Move ${iss.role} back to draft` },
          { value: 'skip', label: 'Leave it for now' },
        ]

  const markApplied = (id: string) => setApplied((prev) => new Set(prev).add(id))

  const onContinue = () => {
    if (!current || !choice) return
    if (current.kind === 'unfilled-published' && choice === 'assign') {
      const sup = supporters[current.id]
      if (!sup) { setNeedsSupporter(true); return }
      onApply(current, 'assign', sup)
      markApplied(current.id)
    } else if (choice !== 'skip') {
      onApply(current, choice)
      markApplied(current.id)
    }
    setAnnounce(choice === 'skip' ? 'Left for now.' : 'Fix applied.')
    setStepIndex((i) => i + 1)
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-5">
      <div aria-live="polite" className="tw:sr-only">{announce}</div>

      <Button rank="secondary" size="sm" leadingIcon="arrow-narrow-left" onClick={onExit} className="tw:w-fit">
        Back to roster
      </Button>

      <div className="tw:grid tw:items-start tw:gap-6 tw:md:grid-cols-[240px_1fr]">
        <WizardStepper steps={steps} currentIndex={onReview ? -1 : stepIndex} applied={applied} onJump={(i) => setStepIndex(i)} />

        <div className="tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-6 tw:shadow-xs">
          {current ? (
            <div className="tw:flex tw:flex-col tw:gap-4">
              <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
                <h2 ref={headingRef} tabIndex={-1} className="tw:text-lg tw:font-bold tw:text-gray-900 tw:focus:outline-hidden">
                  {shortIssueLabel(current)}
                </h2>
                <span className="tw:whitespace-nowrap tw:text-xs tw:font-medium tw:text-gray-500">Step {stepIndex + 1} of {total}</span>
              </div>

              <div className="tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-warning-200 tw:bg-warning-50 tw:p-3">
                <Icon type="alert-triangle" className="tw:mt-0.5 tw:h-5 tw:w-5 tw:shrink-0 tw:text-warning-600" aria-hidden />
                <p className="tw:text-sm tw:text-warning-800">{current.label}</p>
              </div>

              <fieldset className="tw:flex tw:flex-col tw:gap-2">
                <legend className="tw:mb-1 tw:text-sm tw:font-medium tw:text-gray-700">How do you want to fix this?</legend>
                {optionsFor(current).map((o) => {
                  const checked = choice === o.value
                  return (
                    <label key={o.value} className={clsx('tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:px-3 tw:py-2 tw:text-sm tw:text-gray-700', checked ? 'tw:border-primary-400 tw:bg-primary-50' : 'tw:border-gray-200')}>
                      <input
                        type="radio"
                        name={`fix-${current.id}`}
                        checked={checked}
                        onChange={() => { setChoices((c) => ({ ...c, [current.id]: o.value })); setNeedsSupporter(false) }}
                        className="tw:h-4 tw:w-4 tw:border-gray-300 tw:text-primary-600"
                      />
                      {o.label}
                    </label>
                  )
                })}
              </fieldset>

              {current.kind === 'unfilled-published' && choice === 'assign' && (
                <div className="tw:rounded-lg tw:border tw:border-gray-200 tw:p-2">
                  {supporters[current.id] ? (
                    <div className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-gray-700">
                      <Avatar name={supporters[current.id]!.fullName} />
                      {supporters[current.id]!.fullName}
                      <button type="button" onClick={() => setSupporters((s) => ({ ...s, [current.id]: undefined }))} className="tw:ml-auto tw:text-sm tw:font-medium tw:text-primary-700 tw:hover:underline">
                        Change
                      </button>
                    </div>
                  ) : (
                    <SupporterPicker query={query} setQuery={setQuery} autoFocus={false} onPick={(sup) => setSupporters((s) => ({ ...s, [current.id]: sup }))} />
                  )}
                  {needsSupporter && <p className="tw:mt-1 tw:text-sm tw:text-error-600">Pick a volunteer, or choose another option.</p>}
                </div>
              )}

              <div className="tw:mt-2 tw:flex tw:gap-2">
                <Button rank="secondary" disabled={stepIndex === 0} onClick={() => setStepIndex((i) => Math.max(0, i - 1))}>Back</Button>
                <Button rank="primary" disabled={!choice} onClick={onContinue}>
                  {stepIndex === total - 1 ? 'Finish' : 'Continue'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:py-8 tw:text-center">
              <span aria-hidden className="tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-full tw:bg-success-50">
                <Icon type="check-circle" className="tw:h-6 tw:w-6 tw:text-success-600" />
              </span>
              <h2 ref={headingRef} tabIndex={-1} className="tw:text-lg tw:font-bold tw:text-gray-900 tw:focus:outline-hidden">You worked through every step</h2>
              <p className="tw:text-sm tw:text-gray-600">
                {remaining === 0
                  ? 'The roster has no outstanding issues.'
                  : `${remaining} ${remaining === 1 ? 'issue' : 'issues'} still need attention. You can run through them again.`}
              </p>
              <div className="tw:mt-2 tw:flex tw:gap-2">
                <Button rank="secondary" onClick={() => setStepIndex(0)}>Start over</Button>
                <Button rank="primary" onClick={onExit}>Back to roster</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Export roster (workshopped; auth + real send to follow) -------------

function ExportRosterDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [email, setEmail] = useState('')
  const [format, setFormat] = useState<'pdf' | 'csv'>('pdf')
  const [confirmExternal, setConfirmExternal] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setEmail('')
      setFormat('pdf')
      setConfirmExternal(false)
      setSent(false)
      setError(null)
    }
  }, [open])

  const onSend = () => {
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
    if (!ok) { setError('Enter a valid email address.'); return }
    setError(null)
    setSent(true)
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="lg">
        <Dialog.Header>
          <Dialog.Title>Export roster</Dialog.Title>
          <Dialog.Description className="tw:text-gray-500">
            We email the roster to the address below as a PDF or CSV.
          </Dialog.Description>
        </Dialog.Header>

        {sent ? (
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:py-6 tw:text-center">
            <span aria-hidden className="tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-full tw:bg-success-50">
              <Icon type="check-circle" className="tw:h-6 tw:w-6 tw:text-success-600" />
            </span>
            <p className="tw:text-sm tw:font-medium tw:text-gray-700">Roster sent to {email}.</p>
          </div>
        ) : (
          <div className="tw:flex tw:flex-col tw:gap-4">
            <div className="tw:flex tw:flex-col tw:gap-1">
              <label htmlFor="export-email" className="tw:text-sm tw:font-medium tw:text-gray-700">Send to</label>
              <input
                id="export-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.org"
                aria-invalid={!!error}
                className={clsx('tw:rounded-lg tw:border tw:px-3 tw:py-2 tw:text-base tw:focus:outline-hidden', error ? 'tw:border-error-400 tw:focus:border-error-500' : 'tw:border-gray-400 tw:focus:border-primary-400')}
              />
              {error && <span className="tw:text-sm tw:text-error-600">{error}</span>}
            </div>

            <div className="tw:flex tw:flex-col tw:gap-1">
              <span className="tw:text-sm tw:font-medium tw:text-gray-700">Format</span>
              <div role="group" aria-label="Export format" className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
                {(['pdf', 'csv'] as const).map((f, i) => (
                  <button key={f} type="button" onClick={() => setFormat(f)} aria-pressed={format === f}
                    className={clsx('tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:uppercase', i > 0 && 'tw:border-l tw:border-gray-300', format === f ? 'tw:bg-primary-50 tw:text-primary-700' : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50')}>
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <label className="tw:flex tw:items-start tw:gap-2 tw:text-sm tw:text-gray-700">
              <input type="checkbox" checked={confirmExternal} onChange={(e) => setConfirmExternal(e.target.checked)}
                className="tw:mt-0.5 tw:h-4 tw:w-4 tw:rounded tw:border-gray-300 tw:text-primary-600" />
              This address is outside our organisation, send it anyway.
            </label>
          </div>
        )}

        <Dialog.Footer className="tw:mt-2 tw:gap-2">
          {sent ? (
            <Dialog.Close asChild><Button rank="primary">Done</Button></Dialog.Close>
          ) : (
            <>
              <Dialog.Close asChild><Button rank="secondary">Cancel</Button></Dialog.Close>
              <Button rank="primary" leadingIcon="send" onClick={onSend}>Send roster</Button>
            </>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

// --- Roster header (one wrapped card, like GroupShow / calling sessions) --

function RosterHeader({
  allFilled, overallPct, openSlots, draftCount, issueCount, onAdd, onExport,
}: {
  allFilled: number
  overallPct: number
  openSlots: number
  draftCount: number
  issueCount: number
  onAdd: () => void
  onExport: () => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-5 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-6 tw:shadow-xs">
      <div className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-4">
        <div>
          <h2 className="tw:text-lg tw:font-bold tw:text-gray-900">{EVENT.name}</h2>
          <div className="tw:mt-1 tw:flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-gray-500">
            <Icon type="calendar" className="tw:h-4 tw:w-4" aria-hidden />
            {fmtDayShort(EVENT.dates[0])} to {fmtDayShort(EVENT.dates[EVENT.dates.length - 1])}
          </div>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          <Button rank="secondary" leadingIcon="download-cloud" onClick={onExport}>Export roster</Button>
          <Button rank="primary" leadingIcon="plus" onClick={onAdd}>Add shifts</Button>
        </div>
      </div>
      <div className="tw:flex tw:flex-wrap tw:gap-3">
        <SplitStatCard value={allFilled} label="spots filled">
          <BreakdownLine icon="check-circle" iconColor="tw:text-success-600" label={`${overallPct}% filled`} />
          <BreakdownLine icon="alert-circle" iconColor="tw:text-warning-600" label={`${openSlots} still open`} />
        </SplitStatCard>
        <SimpleStatCard value={draftCount} label="In draft" />
        <SimpleStatCard value={issueCount} label="Issues to fix" />
      </div>
    </div>
  )
}

// --- Page ---------------------------------------------------------------

type Density = 'comfortable' | 'compact'
type Layout = 'list' | 'cards'
type View = 'board' | 'fix'
type StatusFilter = 'all' | 'draft' | 'published'
type CoverageFilter = 'any' | 'needs' | 'full'

const ShiftsManagerIndex = () => {
  const [shifts, setShifts] = useState<Shift[]>(EVENT.shifts)
  const [roleFilter, setRoleFilter] = useState<string[]>(() => readListParam('roles'))
  const [peopleFilter, setPeopleFilter] = useState<string[]>(() => readListParam('people'))
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => (readParam('status') as StatusFilter) || 'all')
  const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>(() => (readParam('coverage') as CoverageFilter) || 'any')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [layout, setLayout] = useState<Layout>('list')
  const [view, setView] = useState<View>('board')
  const [wizardSteps, setWizardSteps] = useState<Issue[]>([])

  const [addOpen, setAddOpen] = useState(false)
  const [addPresetDate, setAddPresetDate] = useState<string | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [editing, setEditing] = useState<Shift | null>(null)
  const [moving, setMoving] = useState<Shift | null>(null)
  const [confirm, setConfirm] = useState<ConfirmState | null>(null)
  const [publishIds, setPublishIds] = useState<string[] | null>(null)

  // Design-exploration toggles (floating dev overlay).
  const [showDesign, setShowDesign] = useState(false)
  const [density, setDensity] = useState<Density>('comfortable')
  const [showValidations, setShowValidations] = useState(true)
  const [stickyFooter, setStickyFooter] = useState(true)

  const compact = density === 'compact'
  const roles = useMemo(() => Array.from(new Set(shifts.map((s) => s.roleName))), [shifts])
  const issues = useMemo(() => (showValidations ? computeIssues(shifts) : []), [shifts, showValidations])
  const issuesByShift = useMemo(() => {
    const map = new Map<string, Issue[]>()
    for (const issue of issues) {
      for (const id of issue.shiftIds) {
        map.set(id, [...(map.get(id) ?? []), issue])
      }
    }
    return map
  }, [issues])

  // --- mutations ---
  // A change to a live (published) shift means its assignees may need re-notifying,
  // so mark it as having changes to publish. Draft shifts aren't live yet, so no flag.
  const markChanged = (sh: Shift): boolean | undefined => (sh.draft ? sh.changed : true)
  const setSlot = (shiftId: string, slotId: string, supporter: Supporter | null) =>
    setShifts((prev) =>
      prev.map((sh) => (sh.id !== shiftId ? sh : { ...sh, slots: sh.slots.map((sl) => (sl.id === slotId ? { ...sl, supporter } : sl)), changed: markChanged(sh) }))
    )
  const fillFirstEmpty = (shiftId: string, supporter: Supporter) =>
    setShifts((prev) =>
      prev.map((sh) => {
        if (sh.id !== shiftId) return sh
        const emptyIdx = sh.slots.findIndex((sl) => !sl.supporter)
        if (emptyIdx < 0) return sh
        return { ...sh, slots: sh.slots.map((sl, i) => (i === emptyIdx ? { ...sl, supporter } : sl)), changed: markChanged(sh) }
      })
    )
  const setDraft = (shiftId: string, draft: boolean) =>
    setShifts((prev) => prev.map((sh) => (sh.id === shiftId ? { ...sh, draft } : sh)))
  const publishShifts = (ids: string[]) =>
    setShifts((prev) => prev.map((sh) => (ids.includes(sh.id) ? { ...sh, draft: false, changed: false } : sh)))
  const openPublish = (ids: string[]) => { if (ids.length) setPublishIds(ids) }
  const publishableIds = shifts.filter((sh) => sh.draft || sh.changed).map((sh) => sh.id)
  const deleteShifts = (ids: string[]) => setShifts((prev) => prev.filter((sh) => !ids.includes(sh.id)))
  const moveShiftToDay = (id: string, newDate: string) =>
    setShifts((prev) => prev.map((sh) => (sh.id === id ? { ...sh, startTime: iso(newDate, timeValue(sh.startTime)), endTime: iso(newDate, timeValue(sh.endTime)), changed: markChanged(sh) } : sh)))
  const confirmDeleteShift = (sh: Shift) => {
    const assigned = filledOf(sh)
    setConfirm({
      title: 'Delete this shift?',
      body: `This removes ${sh.roleName}${assigned > 0 ? ` and unassigns ${assigned} ${assigned === 1 ? 'person' : 'people'}` : ''}. This cannot be undone.`,
      confirmLabel: 'Delete shift',
      onConfirm: () => deleteShifts([sh.id]),
    })
  }
  const confirmDeleteSelected = () => {
    const count = selected.size
    setConfirm({
      title: `Delete ${count} ${count === 1 ? 'shift' : 'shifts'}?`,
      body: 'This removes the selected shifts and unassigns everyone on them. This cannot be undone.',
      confirmLabel: `Delete ${count} ${count === 1 ? 'shift' : 'shifts'}`,
      onConfirm: () => { deleteShifts([...selected]); setSelected(new Set()) },
    })
  }
  const duplicateShift = (sh: Shift) =>
    setShifts((prev) => {
      const copy: Shift = {
        ...sh,
        id: nextId('sh'),
        roleName: `${sh.roleName} (copy)`,
        draft: true,
        slots: sh.slots.map(() => ({ id: nextId('sl'), supporter: null })),
      }
      const at = prev.findIndex((s) => s.id === sh.id)
      return [...prev.slice(0, at + 1), copy, ...prev.slice(at + 1)]
    })
  const editShift = (id: string, f: EditFields) =>
    setShifts((prev) =>
      prev.map((sh) => {
        if (sh.id !== id) return sh
        const date = dateOf(sh.startTime)
        const assigned = sh.slots.filter((s) => s.supporter)
        const empty = sh.slots.filter((s) => !s.supporter)
        const slots = [...assigned, ...empty].slice(0, f.volunteers)
        while (slots.length < f.volunteers) slots.push({ id: nextId('sl'), supporter: null })
        return { ...sh, roleName: f.roleName, startTime: iso(date, f.startTime), endTime: iso(date, f.endTime), allowSelfSelect: f.allowSelfSelect, slots, changed: markChanged(sh) }
      })
    )
  const applyFix = (issue: Issue, choice: FixChoice, supporter?: Supporter) => {
    if (issue.kind === 'overlap') {
      if (choice === 'removeA') setSlot(issue.a.shiftId, issue.a.slotId, null)
      if (choice === 'removeB') setSlot(issue.b.shiftId, issue.b.slotId, null)
    } else {
      if (choice === 'assign' && supporter) fillFirstEmpty(issue.shiftId, supporter)
      if (choice === 'draft') setDraft(issue.shiftId, true)
    }
  }

  const openAdd = (date: string | null) => { setAddPresetDate(date); setAddOpen(true) }
  const startFixWizard = () => { setWizardSteps(issues); setView('fix') }

  const matches = (sh: Shift) => {
    if (roleFilter.length && !roleFilter.includes(sh.roleName)) return false
    if (statusFilter === 'draft' && !sh.draft) return false
    if (statusFilter === 'published' && sh.draft) return false
    if (coverageFilter === 'needs' && coverState(sh) === 'full') return false
    if (coverageFilter === 'full' && coverState(sh) !== 'full') return false
    if (peopleFilter.length && !sh.slots.some((sl) => sl.supporter && peopleFilter.includes(sl.supporter.id))) return false
    return true
  }
  const visible = shifts.filter(matches)

  const allSlots = shifts.reduce((n, sh) => n + sh.slots.length, 0)
  const allFilled = shifts.reduce((n, sh) => n + filledOf(sh), 0)
  const overallPct = allSlots === 0 ? 0 : Math.round((allFilled / allSlots) * 100)
  const openSlots = allSlots - allFilled
  const draftCount = shifts.filter((sh) => sh.draft).length

  // Always grouped by date.
  const groups = useMemo(
    () =>
      EVENT.dates
        .map((date) => ({
          key: date,
          date,
          label: fmtDayLong(date),
          items: visible.filter((s) => dateOf(s.startTime) === date).sort((a, b) => dt(a.startTime).getTime() - dt(b.startTime).getTime()),
        }))
        .filter((g) => g.items.length),
    [visible]
  )

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const toggleGroupSelect = (ids: string[], allSelected: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)))
      return next
    })
  const selectedPublishable = shifts.filter((s) => selected.has(s.id) && (s.draft || s.changed)).map((s) => s.id)

  const activeFilterCount = roleFilter.length + peopleFilter.length + (statusFilter !== 'all' ? 1 : 0) + (coverageFilter !== 'any' ? 1 : 0)
  const clearFilters = () => { setRoleFilter([]); setPeopleFilter([]); setStatusFilter('all'); setCoverageFilter('any') }

  // Mirror filters to the URL so the view is shareable.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search)
      const setOrDel = (k: string, v: string) => (v ? p.set(k, v) : p.delete(k))
      setOrDel('roles', roleFilter.join(','))
      setOrDel('people', peopleFilter.join(','))
      setOrDel('status', statusFilter === 'all' ? '' : statusFilter)
      setOrDel('coverage', coverageFilter === 'any' ? '' : coverageFilter)
      const qs = p.toString()
      window.history.replaceState({}, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname)
    } catch {
      // no-op outside the browser
    }
  }, [roleFilter, peopleFilter, statusFilter, coverageFilter])

  const renderShift = (sh: Shift) => {
    const common: ShiftViewProps = {
      sh,
      issues: issuesByShift.get(sh.id) ?? [],
      selected: selected.has(sh.id),
      onToggleSelect: () => toggleSelect(sh.id),
      onAssign: (slotId, sup) => setSlot(sh.id, slotId, sup),
      onRemove: (slotId) => setSlot(sh.id, slotId, null),
      onPublish: () => openPublish([sh.id]),
      onEdit: () => setEditing(sh),
      onDuplicate: () => duplicateShift(sh),
      onMove: () => setMoving(sh),
      onDelete: () => confirmDeleteShift(sh),
    }
    return layout === 'cards' ? <ShiftCard key={sh.id} {...common} /> : <ShiftListRow key={sh.id} {...common} compact={compact} />
  }

  return (
    <PageLayout.Root>
      <PageLayout.Header heading={view === 'fix' ? 'Fix roster issues' : 'Roster & shifts'} />
      <PageLayout.Body>
        <TooltipProvider>
        {view === 'fix' ? (
          <FixIssuesWizard steps={wizardSteps} remaining={issues.length} onApply={applyFix} onExit={() => setView('board')} />
        ) : (
            <div className="tw:flex tw:flex-col tw:gap-6">
              <RosterHeader
                allFilled={allFilled}
                overallPct={overallPct}
                openSlots={openSlots}
                draftCount={draftCount}
                issueCount={issues.length}
                onAdd={() => openAdd(null)}
                onExport={() => setExportOpen(true)}
              />

              {/* Validation issues */}
              {issues.length > 0 && (
                <Alert type="warning" title={`${issues.length} ${issues.length === 1 ? 'thing needs' : 'things need'} a look`}>
                  <ul className="tw:mt-1 tw:flex tw:flex-col tw:gap-1 tw:text-sm">
                    {issues.map((issue) => (
                      <li key={issue.id} className="tw:flex tw:items-center tw:gap-2">
                        <span aria-hidden className="tw:h-1 tw:w-1 tw:shrink-0 tw:rounded-full tw:bg-warning-500" />
                        {issue.label}
                      </li>
                    ))}
                  </ul>
                  <div className="tw:mt-3">
                    <Button rank="secondary" size="sm" leadingIcon="arrow-narrow-right" onClick={startFixWizard}>
                      Walk me through fixes
                    </Button>
                  </div>
                </Alert>
              )}

              {/* Toolbar */}
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                <div className="tw:w-44">
                  <MultiFilter label="Role" options={roles.map((r) => ({ value: r, label: r }))} selected={roleFilter} onChange={setRoleFilter} />
                </div>
                <div className="tw:w-48">
                  <MultiFilter label="People" options={SUPPORTERS.map((s) => ({ value: s.id, label: s.fullName }))} selected={peopleFilter} onChange={setPeopleFilter} />
                </div>
                <div className="tw:w-44">
                  <SelectField
                    hideLabel
                    label="Status"
                    name="status-filter"
                    options={[
                      { value: 'all', label: 'Any status' },
                      { value: 'published', label: 'Published' },
                      { value: 'draft', label: 'Draft' },
                    ]}
                    value={statusFilter}
                    onValueChange={(v) => setStatusFilter(v as StatusFilter)}
                  />
                </div>
                <div className="tw:w-48">
                  <SelectField
                    hideLabel
                    label="Coverage"
                    name="coverage-filter"
                    options={[
                      { value: 'any', label: 'Any coverage' },
                      { value: 'needs', label: 'Needs people' },
                      { value: 'full', label: 'Fully staffed' },
                    ]}
                    value={coverageFilter}
                    onValueChange={(v) => setCoverageFilter(v as CoverageFilter)}
                  />
                </div>

                {/* Board layout switch (segmented Button, matching GroupShow) */}
                <div role="group" aria-label="Board layout" className="tw:ml-auto tw:grid tw:grid-cols-2 tw:shrink-0 tw:overflow-hidden tw:rounded-lg">
                  <Button rank={layout === 'list' ? 'primary' : 'secondary'} leadingIcon="ordered-list" onClick={() => setLayout('list')} className="tw:rounded-none tw:border-0" aria-pressed={layout === 'list'}>List</Button>
                  <Button rank={layout === 'cards' ? 'primary' : 'secondary'} leadingIcon="copy-06" onClick={() => setLayout('cards')} className="tw:rounded-none tw:border-0" aria-pressed={layout === 'cards'}>Cards</Button>
                </div>
              </div>

              {/* Active filters (chip + clear convention from GroupShow) */}
              {activeFilterCount > 0 && (
                <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                  {roleFilter.map((r) => (
                    <FilterChip key={`role-${r}`} label={`Role: ${r}`} onRemove={() => setRoleFilter((prev) => prev.filter((x) => x !== r))} />
                  ))}
                  {peopleFilter.map((id) => (
                    <FilterChip key={`person-${id}`} label={byId(id)?.fullName ?? 'Unknown'} onRemove={() => setPeopleFilter((prev) => prev.filter((x) => x !== id))} />
                  ))}
                  {statusFilter !== 'all' && (
                    <FilterChip label={`Status: ${statusFilter === 'published' ? 'Published' : 'Draft'}`} onRemove={() => setStatusFilter('all')} />
                  )}
                  {coverageFilter !== 'any' && (
                    <FilterChip label={`Coverage: ${coverageFilter === 'needs' ? 'Needs people' : 'Fully staffed'}`} onRemove={() => setCoverageFilter('any')} />
                  )}
                  <Button rank="link" size="sm" leadingIcon="refresh" onClick={clearFilters}>Clear filters</Button>
                </div>
              )}

              {/* Bulk actions */}
              {selected.size > 0 && (
                <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:rounded-xl tw:border tw:border-primary-200 tw:bg-primary-50 tw:px-4 tw:py-2.5">
                  <span className="tw:text-sm tw:font-semibold tw:text-primary-800">{selected.size} selected</span>
                  <div aria-hidden className="tw:h-4 tw:w-px tw:bg-primary-200" />
                  <Button rank="secondary" size="sm" leadingIcon="check" disabled={!selectedPublishable.length} onClick={() => openPublish(selectedPublishable)}>
                    Publish{selectedPublishable.length ? ` (${selectedPublishable.length})` : ''}
                  </Button>
                  <Button rank="secondary" color="danger" size="sm" leadingIcon="trash-01" onClick={confirmDeleteSelected}>
                    Delete
                  </Button>
                  <button type="button" onClick={() => setSelected(new Set())} className="tw:ml-auto tw:text-sm tw:font-medium tw:text-primary-700 tw:hover:underline">
                    Clear selection
                  </button>
                </div>
              )}

              {/* Roster board */}
              {groups.length === 0 ? (
                <div className="tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:p-10 tw:text-center tw:text-sm tw:text-gray-500">
                  No shifts match these filters.
                </div>
              ) : (
                groups.map((group) => {
                  const ids = group.items.map((s) => s.id)
                  const allSel = ids.length > 0 && ids.every((id) => selected.has(id))
                  const someSel = ids.some((id) => selected.has(id))
                  const dayDraftIds = group.items.filter((s) => s.draft || s.changed).map((s) => s.id)
                  return (
                    <section key={group.key} aria-label={group.label} className="tw:flex tw:flex-col tw:gap-3">
                      <div className="tw:flex tw:items-center tw:gap-2.5 tw:text-sm tw:font-semibold tw:text-gray-700">
                        <input
                          type="checkbox"
                          aria-label={`Select all shifts on ${group.label}`}
                          checked={allSel}
                          ref={(el) => { if (el) el.indeterminate = someSel && !allSel }}
                          onChange={() => toggleGroupSelect(ids, allSel)}
                          className="tw:h-4 tw:w-4 tw:rounded tw:border-gray-300 tw:text-primary-600"
                        />
                        <Icon type="calendar" className="tw:h-4 tw:w-4 tw:text-gray-500" aria-hidden />
                        {group.label}
                        <span className="tw:font-normal tw:text-gray-500">{group.items.length} {group.items.length === 1 ? 'shift' : 'shifts'}</span>
                      </div>
                      {layout === 'cards' ? (
                        <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2 tw:xl:grid-cols-3">
                          {group.items.map(renderShift)}
                        </div>
                      ) : (
                        <div className="tw:divide-y tw:divide-gray-100 tw:overflow-hidden tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:shadow-xs">
                          {group.items.map(renderShift)}
                        </div>
                      )}
                      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                        <Button rank="link" size="sm" leadingIcon="plus" onClick={() => openAdd(group.date)}>
                          Add shift to {fmtDayShort(group.date)}
                        </Button>
                        {dayDraftIds.length > 0 && (
                          <Button rank="link" size="sm" leadingIcon="check" onClick={() => openPublish(dayDraftIds)}>
                            Publish {fmtDayShort(group.date)} ({dayDraftIds.length})
                          </Button>
                        )}
                      </div>
                    </section>
                  )
                })
              )}
            </div>
        )}

        <AddShiftsDialog open={addOpen} onOpenChange={setAddOpen} dates={EVENT.dates} presetDate={addPresetDate} roles={roles} onAdd={(created) => setShifts((prev) => [...prev, ...created])} />
        <EditShiftDialog shift={editing} roles={roles} onOpenChange={(o) => { if (!o) setEditing(null) }} onSave={editShift} />
        <MoveToDayDialog shift={moving} dates={EVENT.dates} onOpenChange={(o) => { if (!o) setMoving(null) }} onMove={moveShiftToDay} />
        <PublishDialog ids={publishIds} shifts={shifts} onOpenChange={(o) => { if (!o) setPublishIds(null) }} onConfirm={(ids) => { publishShifts(ids); setSelected(new Set()) }} />
        <ExportRosterDialog open={exportOpen} onOpenChange={setExportOpen} />
        <ConfirmDialog state={confirm} onOpenChange={(o) => { if (!o) setConfirm(null) }} />

        {/* Design exploration overlay (dev only, not part of the product) */}
        {createPortal(
          <>
            <button
              type="button"
              onClick={() => setShowDesign((s) => !s)}
              className="tw:fixed tw:bottom-4 tw:left-4 tw:z-50 tw:flex tw:items-center tw:gap-2 tw:rounded-full tw:border tw:border-gray-300 tw:bg-white tw:px-3.5 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-600 tw:shadow-md tw:hover:border-primary-400 tw:hover:text-primary-600"
            >
              <Icon type="eye" className="tw:h-4 tw:w-4" aria-hidden />
              {showDesign ? 'Hide design tools' : 'Design tools'}
            </button>

            {showDesign && (
              <div className="tw:fixed tw:bottom-16 tw:left-4 tw:z-50 tw:w-[min(560px,calc(100vw-2rem))] tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:p-5 tw:shadow-xl">
                <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
                  <Icon type="eye" className="tw:h-4 tw:w-4 tw:text-gray-400" aria-hidden />
                  <span className="tw:text-sm tw:font-bold tw:text-gray-700">Design exploration</span>
                  <button type="button" onClick={() => setShowDesign(false)} aria-label="Hide design tools" className="tw:ml-auto tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-300 tw:text-gray-500 tw:hover:text-primary-600">
                    <Icon type="x-close" className="tw:h-4 tw:w-4" aria-hidden />
                  </button>
                </div>
                <p className="tw:mb-4 tw:text-xs tw:leading-relaxed tw:text-gray-500">
                  This panel is for comparing design options and is not part of the product.
                </p>
                <div className="tw:flex tw:flex-wrap tw:gap-x-8 tw:gap-y-4">
                  <SegControl label="Density" value={density} onChange={(v) => setDensity(v as Density)}
                    options={[{ value: 'comfortable', label: 'Comfortable' }, { value: 'compact', label: 'Compact' }]} />
                  <SegControl label="Validations" value={showValidations ? 'on' : 'off'} onChange={(v) => setShowValidations(v === 'on')}
                    options={[{ value: 'on', label: 'Shown' }, { value: 'off', label: 'Hidden' }]} />
                  <SegControl label="Sticky footer" value={stickyFooter ? 'on' : 'off'} onChange={(v) => setStickyFooter(v === 'on')}
                    options={[{ value: 'on', label: 'Shown' }, { value: 'off', label: 'Hidden' }]} />
                </div>
              </div>
            )}
          </>,
          document.body
        )}
        </TooltipProvider>
      </PageLayout.Body>

      {view === 'board' && stickyFooter && (
        <PageLayout.Footer>
          <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
            <p className="tw:text-sm tw:text-gray-600">
              <span className="tw:font-semibold tw:text-gray-900">{allFilled}/{allSlots}</span> spots filled
            </p>
            <div className="tw:flex tw:items-center tw:gap-2">
              {issues.length > 0 && (
                <Button rank="secondary" leadingIcon="alert-triangle" onClick={startFixWizard}>Fix issues ({issues.length})</Button>
              )}
              <Button rank="primary" leadingIcon="check" disabled={publishableIds.length === 0} onClick={() => openPublish(publishableIds)}>
                Publish all{publishableIds.length ? ` (${publishableIds.length})` : ''}
              </Button>
            </div>
          </div>
        </PageLayout.Footer>
      )}
    </PageLayout.Root>
  )
}

function SegControl({
  label, value, options, onChange,
}: {
  label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (v: string) => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-400">{label}</span>
      <div role="group" aria-label={label} className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
        {options.map((o, i) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
            className={clsx(
              'tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium',
              i > 0 && 'tw:border-l tw:border-gray-300',
              value === o.value ? 'tw:bg-primary-50 tw:text-primary-700' : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50'
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default ShiftsManagerIndex
